// Display snapshots only. Transaction preparation and authorization never use this cache.
export function createPortfolioCache() {
  let key;
  let value;
  let pending;
  let generation = 0;
  const listeners = new Set();
  return {
    peek: (scope) => (scope === key ? value : undefined),
    load(scope, loader) {
      if (key !== scope) {
        key = scope;
        value = undefined;
        pending = undefined;
        generation += 1;
      }
      if (value !== undefined) return Promise.resolve(value);
      if (pending) return pending;
      const version = generation;
      const request = Promise.resolve().then(loader).then((result) => {
        if (generation === version) value = result;
        return result;
      }).finally(() => {
        if (pending === request) pending = undefined;
      });
      pending = request;
      return request;
    },
    invalidate() {
      generation += 1;
      value = undefined;
      pending = undefined;
      listeners.forEach((listener) => listener());
    },
    clear() {
      key = undefined;
      this.invalidate();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const portfolioCache = createPortfolioCache();
const seenTransactions = new Set();
let channel;

export function invalidatePortfolio(transactionHash) {
  if (transactionHash) {
    if (seenTransactions.has(transactionHash)) return false;
    seenTransactions.add(transactionHash);
    if (seenTransactions.size > 256) {
      seenTransactions.delete(seenTransactions.values().next().value);
    }
  }
  portfolioCache.invalidate();
  return true;
}

// Only completed mutation responses with a transaction hash qualify. Unsigned XDR,
// pending submissions, failed requests and history GETs must not cause refetches.
export function confirmedTransactionHash(result, method) {
  if (!['post', 'put', 'patch', 'delete'].includes(String(method).toLowerCase())) return null;
  if (result?.success !== true || result.xdr || result.pending) return null;
  const payload = result.data || result;
  if (payload.xdr || payload.pending) return null;
  const status = result.status || payload.status;
  if (typeof status === 'number' && (status < 200 || status >= 300)) return null;
  if (typeof status === 'string' && !['success', 'confirmed', 'completed'].includes(status.toLowerCase())) return null;
  const hash = result.tx || result.hash || result.transactionHash || result.txHash ||
    payload.tx || payload.hash || payload.transactionHash || payload.txHash;
  return typeof hash === 'string' && /^[a-f0-9]{64}$/i.test(hash) ? hash.toLowerCase() : null;
}

export function observeTransaction(result, method, requestToken) {
  // A response from a previous login must not refresh the next user's snapshot.
  if (!requestToken || requestToken !== localStorage.getItem('token')) return;
  const hash = confirmedTransactionHash(result, method);
  if (hash && invalidatePortfolio(hash) && channel) {
    // Notification failures must never turn a successful payment into an error.
    try { channel.postMessage({ hash }); } catch (error) { /* Same-tab invalidation already succeeded. */ }
  }
}

export function startPortfolioNotifications() {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel('portfolio-transactions-v1');
      channel.onmessage = ({ data }) => {
        if (/^[a-f0-9]{64}$/i.test(data?.hash || '')) invalidatePortfolio(data.hash.toLowerCase());
      };
    } catch (error) { channel = undefined; }
  }
  const onStorage = (event) => {
    if (event.key === 'token' || event.key === null) {
      seenTransactions.clear();
      portfolioCache.clear();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('storage', onStorage);
    if (channel) channel.close();
    channel = undefined;
  };
}

export function clearPortfolioSession() {
  seenTransactions.clear();
  portfolioCache.clear();
}
